/**
 * Meeting Simulation Page
 * Real-time AI conversation with Azure OpenAI Realtime API
 */

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { store } from "@/store";
import {
  initConversation,
  setRecording,
  setWaitingResponse,
  addPronunciationScore,
} from "@/store/slices/realtimeConversationSlice";
import { getMeetingSections } from "@/store/slices/meetingSectionSlice";
import { selectModuleById } from "@/store/selectors/meetingModulesSelectors";
import {
  submitAssessment,
  fetchAssessmentFeedback,
} from "@/store/slices/interviewAssessmentSlice";
import { useRealtimeProxy } from "@/hooks/useRealtimeProxy";
import { useDirectPCMRecorder } from "@/hooks/useDirectPCMRecorder";
import { useAzureSpeech } from "@/hooks/useAzureSpeech";
import { float32ToBase64 } from "@/utils/pcmEncoder";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Loader2,
  Mic,
  MicOff,
  Square,
  ArrowLeft,
  MessageSquare,
  Play,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { MeetingSessionManager } from "@/utils/meetingSessionManager";
import { logger } from "@/utils/logger";
import { PreparedExpression } from "@/types/meeting-preparation.types";
import { useProduct } from "@/hooks/useProduct";

const MeetingSimulation = () => {
  const config = useProduct();
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isConversationFinalized, setIsConversationFinalized] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [preparedExpressions, setPreparedExpressions] = useState<
    PreparedExpression[] | null
  >(null);

  // Redux state
  const { sections, loading: sectionsLoading } = useAppSelector(
    (state) => state.meetingSection
  );
  const moduleData = useAppSelector(selectModuleById(meetingId || ""));
  const {
    isConnected,
    isRecording,
    isSpeaking,
    isWaitingResponse,
    currentTurn,
    maxTurns,
  } = useAppSelector((state) => state.realtimeConversation);

  const currentSection = sections[currentSectionIndex];

  // Ensure meeting session exists (create if needed)
  useEffect(() => {
    if (meetingId) {
      const existingSession = MeetingSessionManager.get(meetingId);
      if (!existingSession) {
        // Create session if it doesn't exist
        MeetingSessionManager.create(meetingId);
        logger.log(
          "[MeetingSimulation] Created missing meeting session for:",
          meetingId
        );
      }
    }

    // Cleanup function: clear prepared expressions when leaving simulation
    return () => {
      if (meetingId) {
        try {
          sessionStorage.removeItem(`meeting_expressions_${meetingId}`);
          logger.log(
            "[MeetingSimulation] Cleared prepared expressions from sessionStorage for:",
            meetingId
          );
        } catch (error) {
          logger.error(
            "[MeetingSimulation] Failed to clear prepared expressions from sessionStorage:",
            error
          );
        }
      }
    };
  }, [meetingId]);

  // Load prepared expressions from sessionStorage
  useEffect(() => {
    if (meetingId) {
      try {
        const stored = sessionStorage.getItem(
          `meeting_expressions_${meetingId}`
        );
        if (stored) {
          const expressions = JSON.parse(stored) as PreparedExpression[];
          setPreparedExpressions(expressions);
          logger.log(
            "[MeetingSimulation] Loaded prepared expressions from sessionStorage:",
            expressions.length
          );
        }
      } catch (error) {
        logger.error(
          "[MeetingSimulation] Failed to load prepared expressions from sessionStorage:",
          error
        );
      }
    }
  }, [meetingId]);

  /**
   * Handle conversation end - Submit assessment and navigate to processing page
   */
  const handleConversationEnd = useCallback(async () => {
    // Set finalized state IMMEDIATELY to block UI
    setIsConversationFinalized(true);

    logger.log("[Simulation] Conversation ended, preparing assessment...");

    // Get data from Redux store
    const state = store.getState();
    const { conversationId, transcript, usage, pronunciationScores } =
      state.realtimeConversation;

    logger.log("[Simulation] 📊 Collected data:", {
      conversationId,
      transcriptLength: transcript.length,
      pronunciationScores: pronunciationScores.length,
      usage,
    });

    // Calculate average pronunciation score
    const avgPronScore =
      pronunciationScores.length > 0
        ? pronunciationScores.reduce((sum, s) => sum + s.pronScore, 0) /
          pronunciationScores.length
        : 7.5; // Default fallback

    // Format transcript for assessment API
    const formattedTranscript = transcript.map((msg, index) => ({
      role: msg.role,
      content: msg.content,
      sequence: index,
    }));

    // Get meeting session UUID
    const meetingSession = meetingId
      ? MeetingSessionManager.get(meetingId)
      : null;

    // Get meeting metadata
    const metadata = meetingId
      ? MeetingSessionManager.getMetadata(meetingId)
      : null;

    if (!meetingSession) {
      logger.error(
        "[Simulation] ❌ No meeting session found for meetingId:",
        meetingId
      );
      toast({
        title: config.i18n.meetingSimulation.sessionError,
        description: config.i18n.meetingSimulation.sessionNotFound,
        variant: "destructive",
      });
      navigate(`/meeting/${meetingId}`);
      return;
    }

    if (!metadata) {
      logger.error(
        "[Simulation] ❌ No metadata found for meetingId:",
        meetingId
      );
      toast({
        title: config.i18n.meetingSimulation.sessionError,
        description: config.i18n.meetingSimulation.sessionNotFound,
        variant: "destructive",
      });
      navigate(`/meeting/${meetingId}`);
      return;
    }

    // Prepare assessment payload
    const assessmentPayload = {
      conversation_id:
        conversationId ||
        `conv-${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      question_id: currentSection?.id || meetingId || "",
      pronunciation_assessment: avgPronScore / 10,
      transcript: formattedTranscript,
      usage: usage || {},
      meeting_id: meetingSession,
      meeting_name: metadata.name,
      meeting_category: metadata.category,
    };

    logger.log("[Simulation] 📤 Submitting assessment:", assessmentPayload);

    try {
      // Submit assessment
      const result = await dispatch(
        submitAssessment(assessmentPayload)
      ).unwrap();

      logger.log("[Simulation] ✅ Assessment submitted:", result);

      // Always navigate to completed page which will handle polling
      // Backend returns data immediately but we simulate async flow with polling
      // Pass meetingId as query param so InterviewCompleted can get the session
      navigate(`/interview-completed?meetingId=${meetingId}`);
    } catch (error: any) {
      logger.error("[Simulation] ❌ Failed to submit assessment:", error);

      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit interview assessment",
        variant: "destructive",
      });

      // Still navigate to dashboard on error
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    }
  }, [dispatch, currentSection, meetingId, navigate, toast]);

  // WebSocket connection
  const {
    connect,
    disconnect,
    sendAudioChunk,
    commitAudio,
    primeAudioOutput,
    initializeAudio,
    resetAudioRouting,
    audioPlayer,
  } = useRealtimeProxy({
    section: currentSection,
    onConversationEnd: handleConversationEnd,
  });

  // Check if audio is actually playing (not just if isSpeaking flag is set)
  const [isAudioActuallyPlaying, setIsAudioActuallyPlaying] = useState(false);

  useEffect(() => {
    if (!audioPlayer) {
      setIsAudioActuallyPlaying(false);
      return;
    }

    const interval = setInterval(() => {
      const isPlaying = audioPlayer.isPlaying();
      setIsAudioActuallyPlaying(isPlaying);
    }, 100);

    return () => clearInterval(interval);
  }, [audioPlayer]);

  // Determine if we should keep button disabled to prevent green flash
  // Keep disabled during transitions:
  // 1. Initial connection -> First AI response (until audio starts)
  // 2. User stops recording -> AI response (until audio starts or still waiting)
  // 3. Conversation finalized -> Block all interaction
  // 4. Last turn completed -> Block while IA is speaking or after it finishes
  const isLastTurn = currentTurn >= maxTurns;
  const shouldStayDisabled =
    !isConnected || // Not connected
    isAudioActuallyPlaying || // Audio is playing
    isWaitingResponse || // Waiting for AI response
    (isSpeaking && !isAudioActuallyPlaying) || // isSpeaking but audio not started yet (transition period)
    isConversationFinalized || // Conversation completed, redirecting
    isLastTurn; // Last turn completed - block all user interaction

  const isReallyPlaying = isAudioActuallyPlaying;

  // DEBUG: Log state changes
  useEffect(() => {
    logger.log("[UI] 🎨 Button state:", {
      isConnected,
      isRecording,
      isSpeaking,
      isWaitingResponse,
      isAudioActuallyPlaying,
      shouldStayDisabled,
      buttonColor: isRecording ? "RED" : shouldStayDisabled ? "GRAY" : "GREEN",
      timestamp: Date.now(),
    });
  }, [
    isConnected,
    isRecording,
    isSpeaking,
    isWaitingResponse,
    isAudioActuallyPlaying,
    shouldStayDisabled,
  ]);

  /**
   * Handle audio data streaming callback
   * Sends audio chunks to Azure OpenAI Realtime in real-time
   */
  const handleAudioData = useCallback(
    (chunk: Float32Array): void => {
      if (isConnected) {
        const base64Audio = float32ToBase64(chunk);
        sendAudioChunk(base64Audio);
      }
    },
    [isConnected, sendAudioChunk]
  );

  // Audio recording (24kHz for realtime)
  const {
    isRecording: recorderActive,
    recordingTime,
    startRecording: startRecorder,
    stopRecording: stopRecorder,
    error: recorderError,
  } = useDirectPCMRecorder({
    sampleRate: 24000,
    onAudioData: handleAudioData,
  });

  // Pronunciation assessment (parallel, 16kHz)
  const { assessPronunciation: assessPronunciationAPI } = useAzureSpeech();

  // Load sections on mount
  useEffect(() => {
    if (meetingId) {
      dispatch(getMeetingSections(meetingId));
    }
  }, [meetingId, dispatch]);

  // Detect if coming from dashboard (sessionId vs moduleId)
  const isFromDashboard = useMemo(() => {
    // Check if meetingId looks like a sessionId (UUID format) AND we have prepared expressions
    // This indicates we just completed practice and came here directly
    const isSessionId =
      meetingId &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        meetingId
      );
    return isSessionId && preparedExpressions && preparedExpressions.length > 0;
  }, [meetingId, preparedExpressions]);

  // Clear prepared expressions if this is not a "just completed practice" scenario
  useEffect(() => {
    if (meetingId && !isFromDashboard && preparedExpressions) {
      // If we're in a normal simulation (not from dashboard) and have prepared expressions,
      // this means they were left over from a previous session, so clear them
      try {
        sessionStorage.removeItem(`meeting_expressions_${meetingId}`);
        setPreparedExpressions(null);
        logger.log(
          "[MeetingSimulation] Cleared leftover prepared expressions for normal simulation"
        );
      } catch (error) {
        logger.error(
          "[MeetingSimulation] Failed to clear prepared expressions:",
          error
        );
      }
    }
  }, [meetingId, isFromDashboard, preparedExpressions]);

  /**
   * Load sections on mount (only if not from dashboard)
   */
  useEffect(() => {
    if (meetingId && !isFromDashboard) {
      dispatch(getMeetingSections(meetingId));
    }
  }, [meetingId, isFromDashboard, dispatch]);

  /**
   * Initialize conversation when section loads
   */
  useEffect(() => {
    if (currentSection) {
      logger.log("[Simulation] 🎬 Initializing conversation with:", {
        sectionId: currentSection.id,
        num_interactions: currentSection.num_interactions,
        maxTurns: currentSection.num_interactions,
      });

      dispatch(
        initConversation({
          conversationId: `conv-${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sectionId: currentSection.id,
          maxTurns: currentSection.num_interactions || 2, // Fallback to 2 if missing
        })
      );
    }
  }, [currentSection, dispatch]);
  /**
   * Connect WebSocket when component mounts
   */
  useEffect(() => {
    // Connection is now handled manually via handleStartSimulation
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Start simulation (connect to WebSocket)
   */
  const handleStartSimulation = useCallback(async () => {
    if (currentSection) {
      setHasStarted(true);

      // CRITICAL FIX FOR iOS:
      // Initialize AudioContext IMMEDIATELY while we still have the user gesture context.
      // If we wait for getUserMedia (which is async), the user gesture expires and
      // AudioContext.resume() will fail or be suspended on iOS.
      await initializeAudio();

      // 1. Request microphone permission to switch iOS Audio Session to "PlayAndRecord"
      // This ensures the AudioContext created afterwards picks up the correct sample rate and routing
      try {
        logger.log(
          "[Simulation] 🎤 Requesting mic permission to unlock audio session..."
        );
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        // We don't need the stream yet, just the permission/session switch
        stream.getTracks().forEach((track) => track.stop());
        logger.log(
          "[Simulation] ✅ Mic permission granted, audio session unlocked"
        );
      } catch (err) {
        logger.warn(
          "[Simulation] ⚠️ Failed to get mic permission (audio might be silent on iOS):",
          err
        );
      }

      // 2. Re-prime audio context after mic permission
      await primeAudioOutput();

      // 3. CRITICAL FIX FOR iOS: Force audio routing to speaker AND bypass silent mode
      // Must be called AFTER getUserMedia (which switches to PlayAndRecord/Receiver)
      // and BEFORE connecting/playing audio.
      await resetAudioRouting();

      // 4. Connect to WebSocket
      connect();
    }
  }, [
    currentSection,
    connect,
    initializeAudio,
    primeAudioOutput,
    resetAudioRouting,
  ]);

  /**
   * Start recording user audio
   */
  const handleStartRecording = useCallback(async () => {
    try {
      if (!isConnected) {
        logger.error(
          "[Simulation] ❌ Cannot start recording - not connected to Azure"
        );
        return;
      }

      logger.log("[Simulation] 🎙️ Starting recording...");
      await primeAudioOutput();
      await startRecorder();
      dispatch(setRecording(true));
      logger.log(
        "[Simulation] ✅ Recording started, isConnected:",
        isConnected
      );
    } catch (error) {
      logger.error("[Simulation] ❌ Failed to start recording:", error);
    }
  }, [startRecorder, dispatch, isConnected, primeAudioOutput]);

  /**
   * Stop recording and commit audio
   */
  const handleStopRecording = useCallback(async () => {
    try {
      logger.log("[Simulation] 🛑 Stopping recording...");

      // Get PushStream from recorder (contains ALL recorded audio)
      const pushStream = await stopRecorder();

      // CRITICAL: Set waiting BEFORE setting recording to false
      // This prevents green flash during the gap
      dispatch(setWaitingResponse(true));
      dispatch(setRecording(false));
      logger.log("[Simulation] ✅ Recorder stopped, got PushStream");

      // Wait a bit for last audio chunk to be sent to WebSocket
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Commit audio to Azure OpenAI (non-blocking)
      logger.log("[Simulation] 🎬 Calling commitAudio()...");
      commitAudio();

      // CRITICAL FIX FOR iOS: Force audio routing back to speaker AND maintain silent mode bypass
      // This must be called here because we have a user gesture (click) context
      if (audioPlayer) {
        await audioPlayer.resetRouting();
      }

      // Pronunciation assessment in background (non-blocking)
      logger.log("[Simulation] 🎯 Starting pronunciation assessment...");

      try {
        const result = await assessPronunciationAPI(pushStream, "");

        if (result?.NBest?.[0]?.PronunciationAssessment) {
          const assessment = result.NBest[0].PronunciationAssessment;
          dispatch(
            addPronunciationScore({
              turnIndex: currentTurn,
              accuracyScore: assessment.AccuracyScore || 0,
              fluencyScore: assessment.FluencyScore || 0,
              completenessScore: assessment.CompletenessScore || 0,
              pronScore: assessment.PronScore || 0,
              timestamp: Date.now(),
            })
          );
          logger.log(
            "[Simulation] ✅ Pronunciation score saved:",
            assessment.PronScore
          );
        }
      } catch (err) {
        logger.error("[Simulation] ❌ Pronunciation assessment failed:", err);
      }

      logger.log("[Simulation] ✅ Recording stopped, audio committed");
    } catch (error) {
      logger.error("[Simulation] ❌ Failed to stop recording:", error);
    }
  }, [
    stopRecorder,
    commitAudio,
    dispatch,
    currentTurn,
    assessPronunciationAPI,
    audioPlayer,
  ]);

  /**
   * Handle back navigation
   */
  const handleBack = useCallback(() => {
    if (isRecording) {
      // Stop recording first
      handleStopRecording();
    }
    disconnect();
    navigate(`/meeting/${meetingId}`);
  }, [isRecording, meetingId, navigate, disconnect, handleStopRecording]);

  /**
   * Loading state
   */
  if (sectionsLoading && !isFromDashboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  /**
   * Error state for dashboard sessions
   */
  // if (isFromDashboard) {
  //   return (
  //     <div className="min-h-screen bg-background flex items-center justify-center p-4">
  //       <Card className="max-w-md border-border/50 backdrop-blur-sm bg-card/80">
  //         <div className="p-6 space-y-4">
  //           <h2 className="text-xl font-bold text-foreground">
  //             Practice Complete! 🎉
  //           </h2>
  //           <p className="text-muted-foreground">
  //             You've successfully practiced your expressions. Since you came
  //             from reviewing past sessions, you can now return to the dashboard
  //             to start a new meeting simulation.
  //           </p>
  //           <div className="space-y-3">
  //             <Button onClick={() => navigate("/dashboard")} className="w-full">
  //               <ArrowLeft className="w-4 h-4 mr-2" />
  //               Back to Dashboard
  //             </Button>
  //             <Button
  //               onClick={() => navigate("/meeting-simulator")}
  //               variant="outline"
  //               className="w-full"
  //             >
  //               Start New Simulation
  //             </Button>
  //           </div>
  //         </div>
  //       </Card>
  //     </div>
  //   );
  // }

  /**
   * Loading state for normal meetings
   */
  if (!currentSection && !isFromDashboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  /**
   * Error state
   */
  if (recorderError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md border-border/50 backdrop-blur-sm bg-card/80">
          <div className="p-6 space-y-4">
            <h2 className="text-xl font-bold text-destructive">
              Microphone Error
            </h2>
            <p className="text-muted-foreground">{recorderError}</p>
            <Button onClick={handleBack} variant="outline" className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to={`/meeting/${meetingId}`}>
          <Button variant="ghost" className="mb-8 group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back
          </Button>
        </Link>

        <div className="animate-fade-in space-y-8">
          <div className="text-center space-y-4">
            <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
              {moduleData?.category?.name || "Meeting Simulation"}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
              {moduleData?.module?.module_name || "Real-time Conversation"}
            </h1>
            <p className="text-sm text-muted-foreground">
              Turn {currentTurn} of {maxTurns}
              {" • "}
              {isConnected ? "🟢 Connected" : "🔴 Disconnected"}
            </p>
          </div>

          {/* Prepared Expressions Card - Only show if user went through preparation */}
          {preparedExpressions && preparedExpressions.length > 0 && (
            <Card className="border-border/50 backdrop-blur-sm bg-card/80 animate-scale-in">
              <Accordion type="single" collapsible defaultValue="">
                <AccordionItem value="expressions">
                  <AccordionTrigger className="px-8 py-6">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      <span className="text-lg font-semibold">
                        Prepared Expressions
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-8 pb-6">
                    <p className="text-muted-foreground mb-4">
                      Here are the expressions you prepared for this simulation:
                    </p>
                    <div className="space-y-4 border-l-2 border-primary/30 pl-4">
                      {preparedExpressions.map((expression, index) => (
                        <div key={expression.id} className="space-y-1">
                          <p className="text-sm font-semibold text-primary">
                            Expression {index + 1}
                          </p>
                          <p className="text-foreground leading-relaxed">
                            {expression.expression}
                          </p>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
          )}

          <Card className="border-border/50 backdrop-blur-sm bg-card/80 animate-scale-in">
            <div className="p-8 text-center space-y-8">
              {/* Recording/Start Button */}
              <button
                onClick={
                  !hasStarted
                    ? handleStartSimulation
                    : isRecording
                      ? handleStopRecording
                      : handleStartRecording
                }
                disabled={hasStarted && !isRecording && shouldStayDisabled}
                className={cn(
                  "w-32 h-32 rounded-full mx-auto flex items-center justify-center transition-all",
                  // Start Button State
                  !hasStarted &&
                    "bg-green-500 hover:bg-green-600 hover:shadow-lg hover:scale-105 text-white",
                  // Cinza desabilitado: IA falando ou processando
                  hasStarted &&
                    shouldStayDisabled &&
                    !isRecording &&
                    "bg-gray-400 cursor-not-allowed opacity-50",
                  // Verde: pronto para gravar (só quando NENHUMA condição de desabilitar está ativa)
                  hasStarted &&
                    !isRecording &&
                    !shouldStayDisabled &&
                    "bg-green-500 hover:bg-green-600 hover:shadow-lg hover:scale-105 text-white",
                  // Vermelho: gravando
                  hasStarted &&
                    isRecording &&
                    "bg-red-500 hover:bg-red-600 hover:shadow-lg text-white animate-pulse"
                )}
              >
                {/* Ícone baseado no estado */}
                {!hasStarted && <Play className="w-16 h-16 ml-2" />}
                {hasStarted && shouldStayDisabled && !isRecording && (
                  <MicOff className="w-16 h-16" />
                )}
                {hasStarted && !isRecording && !shouldStayDisabled && (
                  <Mic className="w-16 h-16" />
                )}
                {hasStarted && isRecording && <Square className="w-16 h-16" />}
              </button>

              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  {!hasStarted && "Start Simulation"}
                  {hasStarted &&
                    isRecording &&
                    `Recording... ${recordingTime}s`}
                  {hasStarted && isReallyPlaying && "Host is speaking..."}
                  {hasStarted &&
                    isWaitingResponse &&
                    !isReallyPlaying &&
                    "Loading..."}
                  {hasStarted &&
                    isConversationFinalized &&
                    "Conversation completed! Processing..."}
                  {hasStarted &&
                    isLastTurn &&
                    !isRecording &&
                    !isReallyPlaying &&
                    !isWaitingResponse &&
                    !isConversationFinalized &&
                    "Conversation completed! Processing..."}
                  {hasStarted &&
                    !isRecording &&
                    !isReallyPlaying &&
                    !isWaitingResponse &&
                    !isConversationFinalized &&
                    !isLastTurn &&
                    (currentTurn === 0
                      ? "Click the microphone to speak"
                      : "Click the microphone to speak")}
                </h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {!hasStarted
                    ? "Click to start the conversation"
                    : isConversationFinalized
                      ? "Thank you for completing the conversation"
                      : isLastTurn &&
                          !isRecording &&
                          !isReallyPlaying &&
                          !isWaitingResponse
                        ? "Your responses have been analyzed"
                        : "Click the microphone to speak"}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Processing Overlay - shown when conversation is finalized */}
      {/* {isConversationFinalized && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <Card className="bg-card/90 border-border/50 p-8 space-y-4 max-w-md">
            <div className="flex justify-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-center text-foreground">
              Processing Results
            </h3>
            <p className="text-sm text-muted-foreground text-center">
              Analyzing your interview performance...
            </p>
          </Card>
        </div>
      )} */}
    </div>
  );
};

export default MeetingSimulation;
